import { computed, reactive } from 'vue';
import { isUndefined, uniqueId } from 'lodash-es';

import { getChildrenCountByAge } from '../lib';
import { usePassengersValidation } from './use-passengers-validation';
import type { PassengerChildrenMap, PassengersState } from '../types';

const defaultState: PassengersState = {
  ADT: 2,
  CHD: 0,
  INF: 0,
  children: new Map(),
};

export const usePassengersState = (initialState?: PassengersState) => {
  const passengers = reactive<PassengersState>(
    initialState
      ? {
          ADT: initialState.ADT,
          CHD: initialState.CHD,
          INF: initialState.INF,
          children: new Map(initialState.children),
        }
      : defaultState,
  );

  const isValidAge = (value: number | undefined): value is number =>
    !isUndefined(value);

  const childrenAge = computed(() =>
    Array.from(passengers.children.values())
      .map(({ age }) => age)
      .filter(isValidAge),
  );

  const { isMaxPassengersCount, isMaxChildrenCount, isMaxAdultCount } =
    usePassengersValidation(passengers);

  const hasPassengers = computed(
    () => passengers.ADT + passengers.CHD + passengers.INF > 0,
  );

  const resetPassengersState = () => {
    passengers.ADT = 2;
    passengers.CHD = 0;
    passengers.INF = 0;
    passengers.children.clear();
  };

  const setAdults = (adultCount: number) => {
    passengers.ADT = adultCount;
  };

  const setChildren = (children: PassengerChildrenMap) => {
    const ages = Array.from(children.values())
      .filter(({ age }) => age !== undefined)
      .map(({ age }) => age as number);

    const { childCount, infantCount } = getChildrenCountByAge(ages);

    passengers.children = children;
    passengers.CHD = childCount;
    passengers.INF = infantCount;
  };

  const setChildrenWithAges = (ages: number[]) => {
    const childrenMap = new Map();

    for (const age of ages) {
      childrenMap.set(uniqueId('_passenger-child'), { age, isEditing: false });
    }

    setChildren(childrenMap);
  };

  const setPassengers = (newState: PassengersState) => {
    passengers.ADT = newState.ADT;
    passengers.CHD = newState.CHD;
    passengers.INF = newState.INF;
    passengers.children = newState.children;
  };

  return {
    passengers,
    childrenAge,

    hasPassengers,
    isMaxPassengersCount,
    isMaxAdultCount,
    isMaxChildrenCount,

    resetPassengersState,
    setAdults,
    setChildren,
    setChildrenWithAges,
    setPassengers,
  };
};
